import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Container } from "@mui/material";
import { MediaPlayer, MediaProvider } from "@vidstack/react";
import {
  defaultLayoutIcons,
  DefaultVideoLayout,
} from "@vidstack/react/player/layouts/default";
import { axiosInstance } from "../../utils/tokenSetCookie";

import { useTranslation } from "react-i18next";
import Logo from "../../assets/back2.png";
import { AxiosError } from "axios";
import LoginRestriction from "../../utils/loginRestriction";
import { toast } from "react-toastify";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const VIDEO_BASE_URL = import.meta.env.VITE_VIDEO_BASE_URL;
const CUSTOM_UPLOAD_BASE_URL = import.meta.env.VITE_CUSTOM_UPLOAD_BASE_URL;

type Looptype = {
  animationId: number;
  animationTagName: string;
  bannerUrl: string;
  homepageImageUrl: string;
  videoUrl: string;
  type: "animation" | "video" | "image";
  duration?: number;
  customUrl?: string;
  customTitle?: string;
};

const PlayLoop: React.FC = () => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedMedia, setSelectedMedia] = useState<Looptype | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [_isImageSeconds, setIsImageSeconds] = useState<number>(5);
  const [newPlayloopList, setNewPlayloopList] = useState<Looptype[]>([]);
  const [isRestrictionOpen, setIsRestrictionOpen] = useState(false);


  useEffect(() => {
    const enableFullscreen = () => {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error("Failed to enter fullscreen:", err);
      });

      // Cleanup
      window.removeEventListener("load", enableFullscreen);
    };

    window.addEventListener("load", enableFullscreen);

    return () => {
      window.removeEventListener("load", enableFullscreen);
    };
  }, []);

  useEffect(() => {
    const payload = {
      webSiteId: 1,
    };

    const playLoopAnimationDetail = async (payload: any) => {
      try {
        const response = await axiosInstance.post(
          API_BASE_URL + "animation/loop-settings/selected-videos",
          payload
        );

        if (response.status >= 200 && response.status <= 300) {

          setNewPlayloopList(response.data.data.loopedVideos);
          if (response.data.data.loopAnimationFullScreenStatus === 1) {
            setIsFullscreen(true);
          } else {
            setIsFullscreen(false);
          }
          setIsImageSeconds(response.data.data.loopAnimationDuration);
        }
      } catch (error) {
        const errCode = error as AxiosError<{ status: number }>;
        if (errCode.response && errCode.response.status === 401) {
          setIsRestrictionOpen(true);
        } else {
          toast.error("Something went wrong", {
            position: "top-right",
            autoClose: 1000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
          });
        }
        throw error;
      }
    };
    playLoopAnimationDetail(payload); // Passing the payload as an argument
  }, []);

  useEffect(() => {
    setSelectedMedia(newPlayloopList[currentIndex]);
    const currentMedia = newPlayloopList[currentIndex];
    if (currentMedia?.type === "image") {
      const duration = currentMedia.duration || 5;
      const duration1 = duration == 0 ? 5 : duration;
      const interval = setInterval(() => {
        setCurrentIndex(
          (prevIndex) => (prevIndex + 1) % newPlayloopList.length
        );
      }, duration1 * 1000);

      return () => clearInterval(interval);
    }
  }, [currentIndex, newPlayloopList]);

  useEffect(() => {
    // Request fullscreen if the fullscreen flag is true
    if (isFullscreen && containerRef.current) {
      containerRef.current.requestFullscreen().catch((err) => {
        console.error("Failed to enter fullscreen:", err);
      });
    }
  }, [isFullscreen]);

  const handleVideoEnded = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % newPlayloopList.length);
  };

  return (
    <>
      <Box
        sx={{
          position: "relative",
          width: "100vw",
          height: "100vh", // Full page background
          overflow: "hidden",
        }}
      >
        <Box
          aria-label="Logo Background"
          sx={{
            opacity: 0.2,
            position: "absolute",
            background: `url(${Logo}) repeat`,
            width: "100%",
            height: "100%",
            top: 0,
            left: 0,
            zIndex: -1,
          }}
        />
        <Box
          ref={containerRef}
          sx={{
            width: isFullscreen ? "100vw" : "800px", // 100vw when fullscreen, else 800px
            height: isFullscreen ? "100vh" : "600px", // 100vh when fullscreen, else 600px
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "black",
            overflow: "hidden",
            margin: "0 auto",
            marginTop: isFullscreen ? "0" : "10%", // Remove marginTop in fullscreen
            borderRadius: isFullscreen ? "0" : "5px", // Remove border radius in fullscreen
          }}
        >
          <Container
            maxWidth={false}
            disableGutters
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              zIndex: 9,
            }}
          >
            {selectedMedia && (
              <>
                <Typography
                  variant="h6"
                  sx={{
                    padding: 2,
                    fontWeight: "bold",
                    backgroundColor: "#1976d2",
                    color: "white",
                    textAlign: "center",
                  }}
                >
                  {selectedMedia.animationTagName
                    ? t(selectedMedia.animationTagName)
                    : selectedMedia.customTitle}
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  {selectedMedia.type === "animation" ? (
                    <MediaPlayer
                      key={selectedMedia.animationId}
                      src={VIDEO_BASE_URL + selectedMedia.videoUrl}
                      onEnded={handleVideoEnded}
                      autoPlay
                      style={{
                        width: "100%", // Takes full width of the container
                        height: "100%", // Takes full height of the container
                        maxWidth: isFullscreen ? "100%" : "800px", // For non-fullscreen, limit to 800px
                        maxHeight: isFullscreen ? "100%" : "600px", // For non-fullscreen, limit to 600px
                      }}
                    >
                      <MediaProvider>
                        <DefaultVideoLayout icons={defaultLayoutIcons} />
                      </MediaProvider>
                    </MediaPlayer>
                  ) : selectedMedia.type === "video" ? (
                    <MediaPlayer
                      key={selectedMedia.animationId}
                      src={CUSTOM_UPLOAD_BASE_URL + selectedMedia.customUrl}
                      onEnded={handleVideoEnded}
                      autoPlay
                      style={{
                        width: "100%", // Takes full width of the container
                        height: "100%", // Takes full height of the container
                        maxWidth: isFullscreen ? "100%" : "800px", // For non-fullscreen, limit to 800px
                        maxHeight: isFullscreen ? "100%" : "600px", // For non-fullscreen, limit to 600px
                      }}
                    >
                      <MediaProvider>
                        <DefaultVideoLayout icons={defaultLayoutIcons} />
                      </MediaProvider>
                    </MediaPlayer>
                  ) : (
                    <img
                      src={
                        selectedMedia.customUrl
                          ? CUSTOM_UPLOAD_BASE_URL + selectedMedia.customUrl
                          : CUSTOM_UPLOAD_BASE_URL + selectedMedia.bannerUrl ||
                            "/placeholder.svg"
                      }
                      alt={t(selectedMedia.animationTagName)}
                      style={{
                        maxWidth: "100%",
                        maxHeight: isFullscreen ? "100%" : "600px", // Ensure image fits within 600px when not fullscreen
                        objectFit: "contain",
                      }}
                    />
                  )}
                </Box>
              </>
            )}
            <Box p={2} bgcolor="grey.200" textAlign="center">
              <Typography
                variant="subtitle1"
                sx={{ fontSize: { xs: "12px", md: "14px" } }}
              >
                184/2, {t("city")}, {t("state")}, {t("pincode")}
              </Typography>
              <Typography sx={{ fontSize: { xs: "12px", md: "14px" } }}>
                9503503906
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>
      <LoginRestriction open={isRestrictionOpen} />
    </>
  );
};

export default PlayLoop;
